import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { NeedsMeal } from "./Outings";

/**
 * ### 外出外泊届け 設定 API 操作クラス
 */
export default class OutingSettings {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET OutingSetting
   */
  public async get(userStore: LoginUser): Promise<OutingSettingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OutingSetting, formatParams);

    const response = await this.commonService.executeGet<OutingSettingGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH OutingSetting
   */
  public async patch(userStore: LoginUser, requestBody: OutingSettingPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OutingSetting, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OutingSettingGetModel {
  /** 施設を特定するためのID */
  facilityId: number;

  /** 受付状態 */
  canApplication: CanApplication;
  /** 外泊可能日 */
  outingAvailableWeekDay: OutingAvailableWeekDay;
  /** 外出外泊可能時間（最大5） */
  outingAvailableTimes: Array<AvailableTime>;
  /** 届出可能日 */
  requestAvailableDate: RequestAvailableDate;
  /** 外泊最大泊数（13泊まで） */
  stayLimit: number;
  /** 施設での食事の使用有無 */
  useNeedsMeal: NeedsMeal;
  /** 外出手段の選択の使用有無 */
  useSelectedVehicle: boolean;
  /** 外出手段の選択肢の使用有無 */
  useVehicleOptions: UseVehicleOptions;
  /** 自動承諾 */
  autoApproval: boolean;
  /** 家族ユーザーへの案内（1000文字） */
  description: string;
  /** 家族からの連絡事項の使用有無 */
  useText: boolean;

  /** 削除フラグ */
  deleteFlag: boolean;
  /** レコード作成日 */
  created: number;
  /** レコード更新日 */
  updated: number;
}

/** 受付状態 */
export interface CanApplication {
  /** 外出 */
  goingOut: boolean;
  /** 外泊 */
  stayingOut: boolean;
}

export interface OutingAvailableWeekDay {
  /** インデックスシグネチャを追加して、OutingAvailableWeekDay[key名（string）]でアクセスできるようにする */
  [key: string]: boolean;
  /** 日曜日 */
  sunday: boolean;
  /** 月曜日 */
  monday: boolean;
  /** 火曜日 */
  tuesday: boolean;
  /** 水曜日 */
  wednesday: boolean;
  /** 木曜日 */
  thursday: boolean;
  /** 金曜日 */
  friday: boolean;
  /** 土曜日 */
  saturday: boolean;
}

export interface AvailableTime {
  /** 外出外泊可能時間（開始）（hhmmの4桁文字列） */
  start: string;
  /** 外出外泊可能時間（終了）（hhmmの4桁文字列） */
  end: string;
}

export enum RequestAvailableDate {
  /** 当日可 */
  TODAY = 0,
  /** 前日まで */
  NEXT_DAY = 1,
  /** 3日前まで */
  THREE_DAYS = 3,
  /** 5日前まで */
  FIVE_DAYS = 5,
  /** 1週間前まで */
  ONE_WEEK = 7
}

export interface OutingSettingPatchModel {
  /** 受付状態 */
  canApplication: CanApplication;
  /** 外泊可能日 */
  outingAvailableWeekDay: OutingAvailableWeekDay;
  /** 外出外泊可能時間（最大5） */
  outingAvailableTimes: Array<AvailableTime>;
  /** 届出可能日 */
  requestAvailableDate: RequestAvailableDate;
  /** 外泊最大泊数（13泊まで） */
  stayLimit: number;
  /** 施設での食事の使用有無 */
  useNeedsMeal: NeedsMeal;
  /** 外出手段の選択の使用有無 */
  useSelectedVehicle: boolean;
  /** 外出手段の選択肢の使用有無 */
  useVehicleOptions: UseVehicleOptions;
  /** 自動承諾 */
  autoApproval: boolean;
  /** 家族ユーザーへの案内（1000文字） */
  description: string;
  /** 家族からの連絡事項の使用有無 */
  useText: boolean;

  /** レコード更新日 */
  updated: number;
}

export interface UseVehicleOptions {
  /** インデックスシグネチャを追加して、UseVehicleOptions[key名（string）]でアクセスできるようにする */
  [key: string]: boolean;
  /** 家族等の車 */
  familyCar: boolean;
  /** 施設の車 */
  facilityCar: boolean;
  /** タクシー */
  taxi: boolean;
}
